import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
} from '@heroui/react';
import { Languages } from 'lucide-react';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 6px;
  background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const Title = styled.h3`
  font-size: 1.5rem;
  font-weight: 300;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#ffffff' : '#111827')};
  margin-bottom: 8px;
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  line-height: 1.6;
  margin-bottom: 24px;
`;

const InfoBox = styled.div`
  background: ${({ theme }) => (theme.mode === 'dark' ? '#1f2937' : '#f3f4f6')};
  border-left: 3px solid #3B82F6;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
`;

const InfoText = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#d1d5db' : '#4b5563')};
  line-height: 1.5;
  margin: 0;
`;

const LANGUAGE_NAMES: Record<string, string> = {
  pl: 'Polski',
  en: 'English',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français',
  it: 'Italiano',
  cz: 'Čeština',
  sk: 'Slovenčina',
  ua: 'Українська',
  ru: 'Русский',
  gr: 'Ελληνικά',
  se: 'Svenska',
  no: 'Norsk',
};

interface ModalInitialLanguageSetupProps {
  isOpen: boolean;
  onClose?: () => void;
  onConfirm: (language: string) => void;
  availableLanguages: string[];
  defaultLanguage?: string;
  isLoading?: boolean;
}

export const ModalInitialLanguageSetup: FC<ModalInitialLanguageSetupProps> = ({
  isOpen,
  onClose,
  onConfirm,
  availableLanguages,
  defaultLanguage,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    defaultLanguage || availableLanguages[0] || 'en',
  );

  const handleConfirm = () => {
    if (selectedLanguage) {
      onConfirm(selectedLanguage);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      backdrop="opaque"
      size="md"
      isDismissable={false}
      hideCloseButton
      classNames={{
        backdrop: 'bg-black/60 backdrop-blur-sm',
        base: 'bg-white dark:bg-[#1a1f26] rounded-md shadow-2xl',
      }}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1 pb-2 pt-6 px-6">
              <IconWrapper>
                <Languages size={28} className="text-white" />
              </IconWrapper>
              <Title>
                {t('language.welcomeSetup')}
              </Title>
            </ModalHeader>

            <ModalBody className="px-6">
              <Description>
                {t('language.welcomeSetupDescription')}
              </Description>

              <InfoBox>
                <InfoText>
                  {t('language.welcomeSetupInfo')}
                </InfoText>
              </InfoBox>

              <Select
                label={t('language.selectYourLanguage')}
                placeholder={t('language.chooseLanguage')}
                selectedKeys={selectedLanguage ? [selectedLanguage] : []}
                onSelectionChange={(keys) => {
                  const selected = Array.from(keys)[0] as string;
                  setSelectedLanguage(selected);
                }}
                variant="bordered"
                radius="sm"
                size="lg"
                isDisabled={isLoading}
                classNames={{
                  trigger: 'min-h-14 py-3',
                  label: 'font-medium',
                }}
                isRequired
              >
                {availableLanguages.map((lang) => (
                  <SelectItem key={lang}>
                    {LANGUAGE_NAMES[lang] || lang.toUpperCase()}
                  </SelectItem>
                ))}
              </Select>
            </ModalBody>

            <ModalFooter className="px-6 pb-6 pt-4">
              <Button
                color="primary"
                onPress={handleConfirm}
                isLoading={isLoading}
                isDisabled={!selectedLanguage}
                className="w-full bg-[#3B82F6] hover:bg-[#2563EB] font-medium"
                size="lg"
              >
                {t('language.continueWithLanguage')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
